<template>
  <div class="site-header-container">
    <header class="site-header">
      <div class="site-header__container container grid">
        <!-- Left -->
        <div class="site-header__left col xs1 l3 xl3">
          <!-- Logo - desktop -->
          <component :is="titleElement">
            <a
              class="site-header__logo"
              :href="$variable('routes.root')"
            >
              <span
                class="visually-hidden"
                v-text="$variable('store.shopName')"
              />

              <brand-logo />
            </a>

            <a
              class="site-header__trade-title text-other-label-l"
              :href="$variable('routes.root')"
              v-text="$string('general.site_header.trade_title')"
            />
          </component>
        </div>

        <div class="site-header__center col xs4 l5 xl6">
          <!-- Logo - mobile -->
          <component :is="titleElement">
            <a
              class="site-header__logo"
              :href="$variable('routes.root')"
            >
              <span
                class="visually-hidden"
                v-text="$variable('store.shopName')"
              />

              <brand-logo />
            </a>

            <a
              class="site-header__trade-title text-other-label-xs"
              :href="$variable('routes.root')"
              v-text="$string('general.site_header.trade_title')"
            />
          </component>

          <search-bar
            ref="searchBar"
            class="site-header__search-bar"
            :namespace="liquid.sectionId"
            redirect
            :search-query="searchQuery"
            :is-overlay-active="isSearchOverlayActive"
            @search-input="(input) => searchQuery = input"
            @clear="clearSearch"
            @search-click="openSearchOverlay"
            @close="closeSearchOverlay"
          />
        </div>

        <!-- Right -->
        <div class="site-header__right col xs1 l4 xl3">
          <div class="site-header__multi-store">
            <multi-store-form />
          </div>

          <a
            class="site-header__account icon-button"
            :href="accountRedirectUrl"
          >
            <span
              class="visually-hidden"
              v-text="$string('general.site_header.account')"
            />

            <icon-account />
          </a>

          <a
            v-if="$variable('settings.wishlistEnable')"
            class="site-header__wishlist icon-button"
            :href="wishlistPage"
          >
            <span
              class="visually-hidden"
              v-text="$string('general.site_header.wishlist')"
            />

            <icon-wishlist />
          </a>

          <a
            class="site-header__cart icon-button"
            :class="{ 'is-pulsing': cartIconPulsing }"
            :href="$variable('routes.cart.url')"
            @click="handleCartToggle"
          >
            <span
              class="visually-hidden"
              v-text="$string('general.site_header.cart')"
            />

            <icon-cart />

            <span
              class="site-header__cart-badge critical-clear"
              aria-hidden="true"
              v-text="cart.item_count"
            />
          </a>

          <button
            v-if="mobile.handle"
            class="site-header__menu icon-button"
            type="button"
            @click.prevent="handleMenuToggle"
          >
            <icon-menu />
            <span v-text="$string('general.site_header.menu')" />
          </button>
        </div>
      </div>

      <div class="site-header__search container">
        <!-- Search bar - mobile -->
        <search-bar
          v-if="isMobile"
          ref="searchBar"
          class="site-header__search-bar"
          :namespace="liquid.sectionId"
          redirect
          :search-query="searchQuery"
          :is-overlay-active="isSearchOverlayActive"
          @search-input="(input) => searchQuery = input"
          @clear="clearSearch"
          @search-click="openSearchOverlay"
          @close="closeSearchOverlay"
        />
      </div>
    </header>

    <!-- Search -->
    <overlay
      key="predictiveSearch"
      block-class="predictive-search-overlay"
      direction="down"
      namespace="predictiveSearch"
      :template="false"
      type="modal"
    >
      <template #body="overlay">
        <predictive-search
          :namespace="liquid.sectionId"
          :overlay="overlay"
          :search-query="searchQuery"
          @clear="clearSearch"
          @search-input="(input) => searchQuery = input"
        />
      </template>
    </overlay>

    <!-- Menu drawer -->
    <teleport
      v-if="mobile.handle"
      to="body"
    >
      <overlay
        key="menuDrawer"
        block-class="menu-drawer-overlay"
        direction="right"
        namespace="menuDrawer"
        type="drawer"
        :show-close="false"
      >
        <template #body="overlay">
          <menu-drawer
            v-if="getComponentVisible('menu-drawer')"
            v-cloak
            :handle="mobile.handle"
            :collectors-club-url="liquid.collectorsClubUrl"
            :item-count="mobile.itemCount"
            :overlay="overlay"
          />

          <!-- Menu drawer loading placeholder -->
          <div
            v-if="!getComponentActive('menu-drawer')"
            class="site-header__menu-drawer critical-component-hide"
          >
            <div class="site-header__menu-navigation">
              <div class="site-header__menu-drawer-menus">
                <ul class="site-header__menu-drawer-menu">
                  <template
                    v-for="(_, index) of [...Array(mobile.itemCount + 1)]"
                    :key="`menu-drawer-loading-${index}`"
                  >
                    <li class="site-header__menu-drawer-menu-item">
                      <div
                        class="
                          site-header__menu-drawer-link
                          text-body-m-regular
                        "
                      >
                        <span class="site-header__menu-drawer-loading" />
                      </div>
                    </li>
                  </template>
                </ul>
              </div>
            </div>
          </div>
        </template>
      </overlay>
    </teleport>
  </div>
</template>

<script>

/**
 * Vue: Site header (site-header)
 * -----------------------------------------------------------------------------
 * Site header central banner containing logo, icon buttons, and menu drawer.
 * - No style import as it's included in critical stylesheet.
 *
 * @param {Object} liquid - Liquid section ID, used for theme editor events.
 * @param {Object} mobile - Mobile linklist handle and item count.
 * @param {String} titleElement - HTML element of site logo based on page.
 *
 */
// eslint-disable-next-line import/named
import { defineAsyncComponent } from 'vue'
import { mapActions, mapGetters, mapState } from 'vuex'

import DividerLine from '~async/utils/divider-line/divider-line'
import MultiStoreForm from '~global/multi-store-form/multi-store-form'
import Overlay from '~global/overlay/overlay'
import SearchBar from '~global/search-bar/search-bar'
import PredictiveSearch from '~async/predictive-search/predictive-search'

import BrandLogo from '~icons/general/brand-logo.svg'
import IconAccount from '~icons/general/account.svg'
import IconCart from '~icons/general/cart.svg'
import IconMenu from '~icons/general/menu.svg'
import IconWishlist from '~icons/general/wishlist.svg'

export default {
  name: 'SiteHeader',

  components: {
    BrandLogo,
    DividerLine,
    IconAccount,
    IconCart,
    IconMenu,
    IconWishlist,
    MenuDrawer: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.menu-drawer' */
        '~async/menu-drawer/menu-drawer'
      ),
    }),
    MultiStoreForm,
    Overlay,
    SearchBar,
    PredictiveSearch,
  },

  props: {
    // Needed for theme editor events
    // eslint-disable-next-line vue/no-unused-properties
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
        collectorsClubUrl: '',
      }),
    },
    mobile: {
      type: Object,
      default: () => ({
        handle: 'main-menu',
        itemCount: 5,
      }),
    },
    accountRedirectUrl: {
      type: String,
      default: '/account',
    },
    titleElement: {
      type: String,
      default: 'h1',
      validator: (value) => {
        return ['h1', 'span'].includes(value)
      },
    },
  },

  data() {
    return {
      cartIconPulsing: false,
      searchQuery: '',
      wishlistPage: window.cnvs?.settings?.wishlistPage,
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
      getComponentExists: 'components/getExists',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      cart: (state) => state.cart.response,
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Returns true if search overlay is active.
     * @returns {Boolean}
     */
    isSearchOverlayActive() {
      return this.getActiveOverlay?.namespace === 'predictiveSearch'
    },
  },

  watch: {

    /**
     * Watch active overlay to focus search bar.
     */
    async getActiveOverlay(newOverlay, oldOverlay) {
      // Pulse cart icon when cart drawer closes
      if (oldOverlay?.namespace === 'cartDrawer' && newOverlay?.namespace !== 'cartDrawer') {
        this.cartIconPulsing = true
        setTimeout(() => {
          this.cartIconPulsing = false
        }, 2000)
      }

      if (
        !this.isSearchOverlayActive
      ) {
        return
      }

      // Focus search bar input after overlay opened.
      await this.$nextTick()
      setTimeout(() => {
        if (!this.$refs.searchBar.$refs.input) {
          return
        }

        this.$refs.searchBar.$refs.input.focus()
      }, this.$timing('slow'))
    },
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
      openOverlay: 'overlays/open',
      toggleOverlay: 'overlays/toggle',
    }),

    /**
     * Handle menu toggle.
     */
    handleMenuToggle() {
      this.toggleOverlay({
        component: 'menu-drawer',
        ignoreDismissed: true,
        namespace: 'menuDrawer',
      })
    },

    /**
     * Handle cart toggle.
     * - Prevents opening cart drawer on cart page.
     * @param {Object} event - Click event.
     */
    handleCartToggle(event) {
      if (
        this.$variable('page.type') === 'cart' ||
        !this.getComponentExists('cart-drawer')
      ) {
        return
      }

      event.preventDefault()

      this.toggleOverlay({
        component: 'cart-drawer',
        ignoreDismissed: true,
        namespace: 'cartDrawer',
      })
    },

    /**
     * Shopify section editor select event.
     */
    shopifySectionSelect() {
      if (!this.isMobile) {
        return
      }

      this.openOverlay({
        component: 'menu-drawer',
        ignoreDismissed: true,
        namespace: 'menuDrawer',
      })
    },

    /**
     * Shopify section editor deselect event.
     */
    shopifySectionDeselect() {
      this.closeOverlay('menuDrawer')
    },

    /**
     * Open predictive search overlay on search submit.
     */
    openSearchOverlay() {
      if (this.isSearchOverlayActive) {
        return
      }

      const focusContainer = []
      if (this.$refs?.searchBar?.$el) {
        focusContainer.push(this.$refs.searchBar.$el)
      }

      this.openOverlay({
        component: 'predictive-search',
        focusElements: {
          container: focusContainer,
          prepend: true,
        },
        ignoreDismissed: true,
        namespace: 'predictiveSearch',
      })
    },

    /**
     * Close predictive search overlay on search submit.
     */
    async closeSearchOverlay() {
      this.closeOverlay('predictiveSearch')
      this.clearSearch()

      // Blur search bar input after overlay opened.
      await this.$nextTick()
      setTimeout(() => {
        this.$refs.searchBar.$refs.input.blur()
      }, this.$timing('slow'))
    },

    /**
     * Clear search query.
     */
    clearSearch() {
      this.searchQuery = ''
    },
  },
}
</script>
